import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { Alert, Box, Grid, Paper, Typography } from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { SCUILoadingLinear } from '../../../../atomic/atoms';
import { SCUICustomDataGrid } from '../../../../atomic/molecules';
import { DeliveryQuery } from '../../../../services/query';
import TimeHelper from '../../../../utils/time';
import { CSH } from './customerShippingHistory.classes';

interface IProps {
    customerData?: any;
}

const toObjectList = (value: unknown): any[] => {
    if (value == null) return [];

    if (Array.isArray(value)) {
        return value.filter((item) => item && typeof item === 'object');
    }

    if (typeof value === 'object') {
        return [value];
    }

    return [];
};

const toHistoryList = (res: unknown): any[] => {
    if (res == null || typeof res !== 'object') return [];

    const root = res as Record<string, unknown>;
    const historyRecords = root.historyRecords ?? root.HistoryRecords;

    return toObjectList(historyRecords);
};

const mapHistoryItem = (item: any, index: number) => ({
    id: `${item.orderNumber ?? item.OrderNumber ?? index}-${item.shippingListNo ?? item.ShippingListNo ?? index}-${index}`,
    orderNumber: item.orderNumber ?? item.OrderNumber ?? '-',
    shippingListNo: item.shippingListNo ?? item.ShippingListNo ?? '-',
    orderDate: item.orderDate || item.OrderDate ? TimeHelper.formatDateHour(item.orderDate ?? item.OrderDate) : '-',
    oldShippingCompanyName: item.oldShippingCompanyName ?? item.OldShippingCompanyName ?? '-',
    newShippingCompanyName: item.newShippingCompanyName ?? item.NewShippingCompanyName ?? '-',
    changeDate:
        item.changeDate || item.ChangeDate ? TimeHelper.formatDateHour(item.changeDate ?? item.ChangeDate) : '-',
    operatorName: item.operatorName ?? item.OperatorName ?? '-',
    changeDateRaw: item.changeDate ?? item.ChangeDate,
});

export const CustomerShippingHistory: FC<IProps> = ({ customerData }) => {
    const { t } = useTranslation();
    const [records, setRecords] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const columns: ExtendedGridColDef[] = useMemo(
        () => [
            { field: 'orderNumber', headerName: t('customerInformation.orderNumber'), flex: 1, minWidth: 120 },
            { field: 'shippingListNo', headerName: t('customerInformation.shippingListNo'), flex: 1, minWidth: 120 },
            { field: 'orderDate', headerName: t('customerInformation.orderDate'), flex: 1, minWidth: 150 },
            {
                field: 'oldShippingCompanyName',
                headerName: t('customerInformation.oldShippingCompanyName'),
                flex: 1,
                minWidth: 150,
            },
            {
                field: 'newShippingCompanyName',
                headerName: t('customerInformation.newShippingCompanyName'),
                flex: 1,
                minWidth: 150,
            },
            { field: 'changeDate', headerName: t('customerInformation.updateDate'), flex: 1, minWidth: 150 },
            { field: 'operatorName', headerName: t('customerInformation.updatedByUser'), flex: 1, minWidth: 150 },
        ],
        [t]
    );

    useEffect(() => {
        const customerId = customerData?.customer?.customerId;
        if (!customerId) {
            setRecords([]);
            return;
        }

        setLoading(true);
        const fetchHistory = async () => {
            try {
                const res = await DeliveryQuery.GetCustomerShippingCompanyChangeHistory({ customerId });

                if (res?.isSuccess === false) {
                    Swal.fire({
                        title: t('general.error'),
                        text: res.message || t('general.serviceError'),
                        icon: 'error',
                    });
                    setRecords([]);
                    return;
                }

                const mappedRecords = toHistoryList(res)
                    .map((item, index) => mapHistoryItem(item, index))
                    .sort(
                        (a, b) => new Date(b.changeDateRaw ?? 0).getTime() - new Date(a.changeDateRaw ?? 0).getTime()
                    );
                setRecords(mappedRecords);
            } catch {
                setRecords([]);
            } finally {
                setLoading(false);
            }
        };

        fetchHistory();
    }, [customerData?.customer?.customerId]);

    return (
        <Paper sx={{ padding: '16px' }} className={CSH.page}>
            <Grid container size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalShippingIcon color="primary" />
                    <Typography variant="h6" component="h2" className={CSH.title}>
                        {t('customerInformation.customerShippingChangeHistory')}
                    </Typography>
                </Box>
            </Grid>
            <Grid container size={{ xs: 12 }} sx={{ marginTop: '1rem' }}>
                <Alert severity="info" className={CSH.infoAlert} sx={{ width: '100%' }}>
                    {t('customerInformation.shippingChangeHistoryInfo')}
                </Alert>
            </Grid>
            <Grid container size={{ xs: 12 }} sx={{ marginTop: '1.5rem' }}>
                <SCUILoadingLinear display={loading} />
                {!loading && records.length === 0 ? (
                    <Typography className={CSH.noRecord} sx={{ width: '100%', textAlign: 'center', py: 4 }}>
                        {t('customerInformation.customerNoRecordFound')}
                    </Typography>
                ) : (
                    !loading && (
                        <Box className={CSH.datagrid} sx={{ width: '100%' }}>
                            <SCUICustomDataGrid
                                rows={records}
                                columns={columns}
                                pageSize={10}
                                hideFooter={records.length <= 10}
                            />
                        </Box>
                    )
                )}
            </Grid>
        </Paper>
    );
};
